



















import { Component, Vue } from "vue-property-decorator";
import { MESSAGE } from "@/model/Message";
import { Getter } from "vuex-class";
import { Auth } from "@/service/auth";
import { SessionService } from "@/service/sessionService";
import { AuthContext, ShakenStorage } from "@/service/storage";

type ShakenMode = "factory" | "user";

@Component
export default class App extends Vue {
  appVersion = process.env.VUE_APP_VERSION; // バージョンを埋め込むことで明示的にキャッシュバスターする
  appMode = process.env.VUE_APP_MODE; // ビルドモード(NODE_ENV)とは別に設定した動作モード指示を読み込み
  appTitle = process.env.VUE_APP_TITLE; // 個別にタイトル名を指定する場合に対応
  loadingText: string = MESSAGE.Loading;
  isUserMode: boolean = false;
  private authChangeInProgress: boolean = false;
  private storageListener: any = null;
  private authContextListener: any = null;
  private activeAuthContext: AuthContext | null = null;
  private pendingLoginChangeMode: ShakenMode | null = null;
  private organizationWaitLoading: any = null;

  @Getter("fontSizeLevel", { namespace: "GlobalValue" })
  fontSizeLevelVal: number;

  @Getter("isSessionChecking", { namespace: "Authentication" })
  isSessionChecking: boolean;

  @Getter("sessionConnectionProblem", { namespace: "Authentication" })
  sessionConnectionProblem: string;

  get sessionConnectionProblemLabel(): string {
    if (this.sessionConnectionProblem === "timeout") { return "タイムアウト"; }
    if (this.sessionConnectionProblem === "server") { return "サーバー応答エラー"; }
    return "通信エラー";
  }

  retrySessionCheck() {
    new Auth().retrySessionCheck().then((isAuthenticated: boolean) => {
      if (isAuthenticated) {
        // 通信失敗で不完全になった画面を、認証状態を維持したまま再構築する。
        window.location.reload();
      }
    });
  }

  logout() {
    SessionService.handleUnauthorized();
  }

  created() {
    // このタブがもともと使用している所属情報を保持する。storageイベント発火時は
    // localStorage が既に後ログイン者の値へ置き換わっているため、直接読まない。
    this.activeAuthContext = ShakenStorage.getActiveAuth(ShakenStorage.getMode());
    this.storageListener = (event: StorageEvent) => this.onStorageChanged(event);
    this.authContextListener = (event: CustomEvent) => this.onActiveAuthChanged(event);
    window.addEventListener("storage", this.storageListener);
    window.addEventListener(ShakenStorage.AUTH_CONTEXT_CHANGED_EVENT, this.authContextListener);
    setTimeout(() => {
      if (this.$route.fullPath.indexOf("user") > -1) {
        this.isUserMode = true;
      }

      let refer: any =
        document.querySelector("link[rel*='icon']") ||
        document.createElement("link");
      if (this.isUserMode) {
        refer.href = window.location.origin + "/favicon2.ico";
      } else {
        refer.href = window.location.origin + "/favicon1.ico";
      }
      /** 2021-07-06 sh #7265 set to title. [START] */
      let titleName = '車検PRO';
      if ( typeof(this.appMode) != 'undefined' ) {
        // if( this.appMode === 'production')  { document.title = '車検PRO'; }
        if( this.appMode === 'staging')     { titleName = '【Staging】車検PRO'; }
        if( this.appMode === 'sandbox')     { titleName = '【検証】車検PRO'; }
        if( this.appMode === 'development') { titleName = '【開発】車検PRO'; }
      }
      if ( typeof(this.appTitle) != 'undefined' && this.appTitle !== '') { titleName = this.appTitle; }
      document.title = titleName; // ページ名を設定
      
      /** 2021-07-06 sh #7265 set to title. [END] */
    }, 750);
  }

  destroyed() {
    if (this.storageListener) {
      window.removeEventListener("storage", this.storageListener);
    }
    if (this.authContextListener) {
      window.removeEventListener(ShakenStorage.AUTH_CONTEXT_CHANGED_EVENT, this.authContextListener);
    }
    this.closeOrganizationWait();
  }

  onActiveAuthChanged(event: CustomEvent) {
    const detail = event.detail || {};
    if (detail.mode === ShakenStorage.getMode()) {
      this.activeAuthContext = detail.context || null;
    }
  }

  onStorageChanged(event: StorageEvent) {
    const mode = ShakenStorage.getMode() as ShakenMode;
    if (event.storageArea !== window.localStorage ||
      event.key !== ShakenStorage.getAuthKey(mode)) {
      return;
    }
    if (this.pendingLoginChangeMode === mode) {
      this.resolveAfterLoginStateChanged(mode);
      return;
    }
    if (this.authChangeInProgress) {
      return;
    }

    const current = this.activeAuthContext;
    const currentUserId = (current && current.loginUserId) ||
      this.$store.getters["Authentication/user"];
    const isLoginPage = this.$route.name === "factorylogin" || this.$route.name === "userlogin";
    // ログイン画面・未ログイン状態のタブは、別タブの認証状態を引き継がない。
    if (isLoginPage || !currentUserId) {
      return;
    }

    if (!event.newValue) {
      // 別タブでログアウトされた
      this.showLoginStateChangedDialog(mode);
      return;
    }

    let next: AuthContext;
    try {
      next = JSON.parse(event.newValue) as AuthContext;
    } catch (error) {
      return;
    }
    // Vuexは画面生成順によってまだ初期化されていないことがあるため、
    // 別ID判定の主キーには使わない。ここでVuexの空値を理由にnextを採用すると、
    // 先行タブの比較元まで後ログイン者に置換されてしまう。
    if (!next.loginUserId || currentUserId === next.loginUserId) {
      // 同じ利用者の再ログイン・トークン更新は現在のタブを中断しない。
      ShakenStorage.setActiveAuth(next, mode);
      this.activeAuthContext = next;
      this.$store.dispatch("Authentication/setToken", next.token);
      this.$store.dispatch("Authentication/setUser", next.loginUserId);
      return;
    }

    this.showLoginStateChangedDialog(mode);
  }

  private showLoginStateChangedDialog(mode: ShakenMode): void {
    this.authChangeInProgress = true;
    Auth.suspendCurrentTab();
    // OK後の所属比較に必要な情報だけを残し、tokenは破棄する。
    if (this.activeAuthContext) {
      this.activeAuthContext = { ...this.activeAuthContext, token: "" };
    }
    (this as any).$alert(
      "ログイン状態が変更されました。再読み込みします。",
      "ログイン状態の変更",
      { confirmButtonText: "OK", showClose: false, closeOnClickModal: false, closeOnPressEscape: false }
    ).then(() => {
      this.resolveAfterLoginStateChanged(mode);
    });
  }

  /** ダイアログのOK押下時点の認証状態・所属で遷移先を決定する。 */
  private resolveAfterLoginStateChanged(mode: ShakenMode): void {
    const next = ShakenStorage.getAuth(mode);
    if (!next) {
      // 認証無しの場合はログイン画面
      this.closeOrganizationWait();
      window.location.href = mode === "user" ? "/user/login" : "/factory/login";
      return;
    }

    if (!next.organizationReady) {
      // 認証有り、FunctionSetting無しの場合はFunctionSetting待ち（ログインしたタブで、別のAPIで取得中）
      this.openOrganizationWait(mode);
      return;
    }

    this.closeOrganizationWait();
    if (this.isSameOrganization(this.activeAuthContext, next)) {
      // 同じ所属の場合は再読み込み
      window.location.reload();
      return;
    }
    // 違う所属の場合は予約一覧
    window.location.href = mode === "user" ? "/user/reservationNumber" : "/factory/reservation";
  }

  /** 所属情報の取得完了まで画面操作をブロックする。 */
  private openOrganizationWait(mode: ShakenMode): void {
    if (this.pendingLoginChangeMode === mode) {
      return;
    }
    this.pendingLoginChangeMode = mode;
    this.organizationWaitLoading = (this as any).$loading({
      lock: true,
      text: "ログイン状態を確認しています。",
      background: "rgba(255, 255, 255, 0.9)"
    });
  }

  private closeOrganizationWait(): void {
    this.pendingLoginChangeMode = null;
    if (this.organizationWaitLoading) {
      this.organizationWaitLoading.close();
      this.organizationWaitLoading = null;
    }
  }

  private isSameOrganization(current: AuthContext | null, next: AuthContext): boolean {
    return !!current &&
      !!current.organizationReady &&
      !!current.groupCd && !!current.shatenCd && !!current.kyotenCd &&
      current.groupCd === next.groupCd &&
      current.shatenCd === next.shatenCd &&
      current.kyotenCd === next.kyotenCd;
  }

  get cssFontSize() {
    if (this.fontSizeLevelVal == 1) {
      return "s";
    }
    if (this.fontSizeLevelVal == 3) {
      return "l";
    }

    return "";
  }
}
